/*
 * Conversation Details Report
 * */
import { markdownTable } from 'markdown-table';
import MarkdownIt from 'markdown-it';
import MarkdownItAnchor from 'markdown-it-anchor';
import MarkdownItAttrs from 'markdown-it-attrs';

// Conversation Details Report
export class ConversationDetailsReport {
  constructor() {}

  static generateResultReports(result) {
    const res = {
      success: false,
      reportMd: null,
      reportHtml: null,
    };

    res.reportMd = ConversationDetailsReport.generateMarkdownReport(result);
    res.reportHtml = ConversationDetailsReport.generateHTMLReport(res.reportMd);
    res.success = true;

    return res;
  }

  static formatSeconds(sec) {
    if (sec === undefined || sec === 0) {
      return '00:00:00';
    }
    const totalMinutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  static generateMarkdownReport(result) {
    //const slugify = (s) => encodeURIComponent(String(s).trim().toLowerCase().replace(/\s+/g, '-'));

    let md = '';
    md += '# Conversation Analysis Report\n\n';

    const transcriptMeta = result?.context?.transcript?.metadata || {};
    const metadataTable = [];
    Object.keys(transcriptMeta).map((x) => {
      const v = x === 'duration' ? ConversationDetailsReport.formatSeconds(transcriptMeta[x]) : transcriptMeta[x];
      metadataTable.push([x, v]);
    });
    metadataTable.unshift(['', '']);
    md += markdownTable(metadataTable, { align: ['l', 'l'] });

    md += '\n\n## Summary\n\n';

    md += `${result?.context?.analysis?.summary || ''}`;

    md += '\n\n## Sentiment\n\n';

    md += `#### Customer sentiment: ${result?.context?.analysis?.sentiment?.customerSentiment || 0}\n\n`;

    md += `${result?.context?.analysis?.sentiment?.explanation || ''}`;

    md += '\n\n## Problems\n\n';

    const problems = result?.context?.analysis?.problems || [];
    const problemsData = problems.map((x, i) => [`[${x.problem}](#p-${i})`, x?.severity || x.importance || 0]);
    problemsData.unshift(['Problem', 'Severity']);
    md += markdownTable(problemsData, { align: ['l', 'c'] });
    md += '\n\n';

    problems.map((x, i) => {
      md += `### ${x.problem} {#p-${i}}\n`;

      md += `#### Severity: ${x?.severity || x?.importance || 0}\n\n`;

      md += `**${x.explanation}**\n\n`;
    });

    md += '## KPI\n';

    const kpis = result?.context?.analysis?.kpis || [];
    const kpiData = kpis.map((x, i) => [`[${x.kpi}](#kpi-${i})`, x.score]);
    kpiData.unshift(['KPI', 'Score']);
    md += markdownTable(kpiData, { align: ['l', 'c'] });
    md += '\n\n';

    kpis.map((x, i) => {
      md += `### ${x.kpi} {#kpi-${i}}\n`;

      md += `#### Score: ${x.score}\n\n`;

      md += `>${x.definition}\n\n`;
      md += `**${x.explanation}**\n\n`;
    });

    md += '\n\n## Transcript\n\n';

    const transcriptData = (result?.context?.transcript?.data || []).map((x) => [
      ConversationDetailsReport.formatSeconds(x.at),
      x.actor,
      x.message.replace(/(?:\r\n|\r|\n)/g, '<br/>').replace(/\|/g, '\\|'),
      //x.skip ? 'No' : 'Yes',
    ]);
    transcriptData.unshift(['hh:mm:ss', 'Name', 'Text']);
    md += markdownTable(transcriptData, { align: ['l', 'l', 'l'] });

    return md;
  }

  static generateHTMLReport(markdown) {
    const md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
    })
      .use(MarkdownItAttrs)
      .use(MarkdownItAnchor);

    const htmlReport = md.render(markdown);
    //const css = loadFile('style.css');
    //const logo = loadFile('logo.html');

    let html =
      '<!DOCTYPE html>\n' +
      '<html>\n' +
      '<head>\n' +
      '  <meta charset="utf-8">\n' +
      '  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n' +
      '  <title>Report</title>\n' +
      '  <style>\n' +
      ConversationDetailsReport.getCSS() +
      '</style>\n' +
      '</head>\n' +
      '\n' +
      '<body class="stackedit">\n' +
      '  <div class="stackedit__html">\n' +
      '  <div style="height: 40px;width: 248px;margin-top:10px;">\n' +
      ConversationDetailsReport.getLogo() +
      '  </div>\n' +
      htmlReport +
      '  </div>\n' +
      '</body>\n' +
      '</html>\n';

    return html;
  }

  static getLogo() {
    return (
      '<svg viewBox="0 0 248 40">\n' +
      '  <path\n' +
      '    fill-rule="evenodd"\n' +
      '    clip-rule="evenodd"\n' +
      '    d="M34.728 0.275L2.245 15.267c-3.748 1.749 -2.498 7.246 1.499 7.246h13.743v13.743c0 3.998 5.497 5.248 7.246 1.499l14.992 -32.483c1.249 -2.999 -1.999 -6.248 -4.998 -4.998z"\n' +
      '    fill="#E24D42"\n' +
      '  ></path>\n' +
      '  <path\n' +
      '    fill-rule="evenodd"\n' +
      '    clip-rule="evenodd"\n' +
      '    d="M 83.535 8.75 L 91.016 8.75 L 91.016 11.309 L 87.344 11.992 A 6.127 6.127 0 0 1 88.048 13.305 A 7.142 7.142 0 0 1 88.184 13.682 A 6.256 6.256 0 0 1 88.474 15.162 A 7.488 7.488 0 0 1 88.496 15.742 A 7.852 7.852 0 0 1 88.171 18.06 A 6.129 6.129 0 0 1 86.182 21.025 Q 84.165 22.702 80.821 22.917 A 15.796 15.796 0 0 1 79.805 22.949 A 21.888 21.888 0 0 1 77.939 22.797 A 20.451 20.451 0 0 1 77.91 22.793 A 4.598 4.598 0 0 0 77.394 23.157 Q 77.13 23.372 76.938 23.607 A 2.627 2.627 0 0 0 76.836 23.74 Q 76.465 24.258 76.465 24.902 A 1.213 1.213 0 0 0 76.531 25.311 A 1.038 1.038 0 0 0 76.836 25.752 Q 77.207 26.074 77.94 26.23 A 6.279 6.279 0 0 0 78.534 26.326 Q 78.868 26.364 79.25 26.378 A 12.766 12.766 0 0 0 79.727 26.387 L 83.457 26.387 A 13.454 13.454 0 0 1 85.514 26.534 Q 86.556 26.695 87.399 27.034 A 5.85 5.85 0 0 1 88.887 27.891 A 4.851 4.851 0 0 1 90.614 30.779 A 7.431 7.431 0 0 1 90.762 32.305 A 7.105 7.105 0 0 1 90.278 34.978 Q 89.548 36.794 87.715 37.998 A 10.498 10.498 0 0 1 84.958 39.247 Q 83.628 39.647 82.015 39.835 A 26.66 26.66 0 0 1 78.945 40 Q 76.118 40 74.139 39.345 A 7.834 7.834 0 0 1 72.207 38.418 A 5.392 5.392 0 0 1 70.626 36.824 Q 69.947 35.743 69.873 34.335 A 6.661 6.661 0 0 1 69.863 33.984 A 5.258 5.258 0 0 1 70.166 32.172 A 4.686 4.686 0 0 1 71.113 30.615 A 5.632 5.632 0 0 1 72.802 29.363 Q 73.517 29.008 74.396 28.782 A 10.295 10.295 0 0 1 74.688 28.711 A 3.803 3.803 0 0 1 73.347 27.703 A 4.566 4.566 0 0 1 73.174 27.49 Q 72.539 26.66 72.539 25.645 A 3.579 3.579 0 0 1 72.7 24.554 A 3.044 3.044 0 0 1 73.223 23.574 A 5.242 5.242 0 0 1 73.884 22.892 Q 74.457 22.393 75.274 21.914 Q 73.574 21.191 72.549 19.561 A 6.644 6.644 0 0 1 71.605 16.919 A 8.559 8.559 0 0 1 71.524 15.723 A 9.203 9.203 0 0 1 71.708 13.837 A 6.7 6.7 0 0 1 72.52 11.729 Q 73.516 10.078 75.43 9.209 Q 77.164 8.422 79.555 8.348 A 16.269 16.269 0 0 1 80.059 8.34 A 11.5 11.5 0 0 1 80.672 8.357 A 14.911 14.911 0 0 1 81.338 8.408 Q 82.031 8.477 82.627 8.564 A 11.883 11.883 0 0 1 82.907 8.609 Q 83.301 8.677 83.535 8.75 Z M 43.32 16.289 L 43.32 30.391 L 38.731 30.391 L 38.731 17.148 A 9.793 9.793 0 0 0 38.639 15.763 Q 38.538 15.056 38.324 14.486 A 3.896 3.896 0 0 0 37.695 13.369 A 3.259 3.259 0 0 0 36.067 12.317 Q 35.373 12.109 34.492 12.109 A 7.976 7.976 0 0 0 32.917 12.254 Q 32.025 12.434 31.356 12.838 A 3.695 3.695 0 0 0 30.127 14.043 Q 28.918 15.958 28.907 19.577 A 21.94 21.94 0 0 0 28.906 19.648 L 28.906 30.391 L 24.317 30.391 L 24.317 8.77 L 27.891 8.77 L 28.535 11.699 L 28.789 11.699 A 6.31 6.31 0 0 1 30.537 9.834 Q 31.582 9.102 32.881 8.73 A 9.798 9.798 0 0 1 35.586 8.359 A 11.398 11.398 0 0 1 37.858 8.572 Q 39.23 8.851 40.289 9.497 A 6.266 6.266 0 0 1 41.279 10.244 A 5.691 5.691 0 0 1 42.74 12.5 Q 43.32 14.071 43.32 16.289 Z M 19.141 18.164 L 19.141 20.625 L 4.707 20.625 Q 4.765 22.961 5.669 24.499 A 5.322 5.322 0 0 0 6.397 25.469 Q 8.008 27.168 10.918 27.168 A 18.388 18.388 0 0 0 12.883 27.068 A 13.634 13.634 0 0 0 14.639 26.768 A 18.81 18.81 0 0 0 16.851 26.078 A 22.729 22.729 0 0 0 18.047 25.586 L 18.047 29.316 Q 16.426 30.078 14.746 30.43 A 15.132 15.132 0 0 1 13.211 30.664 Q 12.432 30.745 11.548 30.77 A 28.916 28.916 0 0 1 10.723 30.781 A 13.867 13.867 0 0 1 7.82 30.49 A 10.794 10.794 0 0 1 5.127 29.541 Q 2.715 28.301 1.358 25.84 Q 0.127 23.61 0.012 20.401 A 18.776 18.776 0 0 1 0 19.727 Q 0 16.507 0.967 14.146 A 10.342 10.342 0 0 1 1.231 13.555 Q 2.461 11.016 4.688 9.688 A 9.368 9.368 0 0 1 8.272 8.464 A 12.008 12.008 0 0 1 9.883 8.359 A 11.755 11.755 0 0 1 12.255 8.587 A 8.647 8.647 0 0 1 14.844 9.551 Q 16.914 10.742 18.028 12.93 A 9.888 9.888 0 0 1 18.92 15.621 Q 19.134 16.77 19.141 18.079 A 16.579 16.579 0 0 1 19.141 18.164 Z M 67.598 18.164 L 67.598 20.625 L 53.164 20.625 Q 53.222 22.961 54.126 24.499 A 5.322 5.322 0 0 0 54.854 25.469 Q 56.465 27.168 59.375 27.168 A 18.388 18.388 0 0 0 61.341 27.068 A 13.634 13.634 0 0 0 63.096 26.768 A 18.81 18.81 0 0 0 65.308 26.078 A 22.729 22.729 0 0 0 66.504 25.586 L 66.504 29.316 Q 64.883 30.078 63.203 30.43 A 15.132 15.132 0 0 1 61.668 30.664 Q 60.889 30.745 60.005 30.77 A 28.916 28.916 0 0 1 59.18 30.781 A 13.867 13.867 0 0 1 56.277 30.49 A 10.794 10.794 0 0 1 53.584 29.541 Q 51.172 28.301 49.815 25.84 Q 48.584 23.61 48.469 20.401 A 18.776 18.776 0 0 1 48.457 19.727 Q 48.457 16.507 49.424 14.146 A 10.342 10.342 0 0 1 49.688 13.555 Q 50.918 11.016 53.145 9.688 A 9.368 9.368 0 0 1 56.729 8.464 A 12.008 12.008 0 0 1 58.34 8.359 A 11.755 11.755 0 0 1 60.712 8.587 A 8.647 8.647 0 0 1 63.301 9.551 Q 65.371 10.742 66.485 12.93 A 9.888 9.888 0 0 1 67.377 15.621 Q 67.591 16.77 67.598 18.079 A 16.579 16.579 0 0 1 67.598 18.164 Z M 112.813 18.164 L 112.813 20.625 L 98.379 20.625 Q 98.437 22.961 99.341 24.499 A 5.322 5.322 0 0 0 100.069 25.469 Q 101.68 27.168 104.59 27.168 A 18.388 18.388 0 0 0 106.555 27.068 A 13.634 13.634 0 0 0 108.311 26.768 A 18.81 18.81 0 0 0 110.523 26.078 A 22.729 22.729 0 0 0 111.719 25.586 L 111.719 29.316 Q 110.098 30.078 108.418 30.43 A 15.132 15.132 0 0 1 106.883 30.664 Q 106.104 30.745 105.22 30.77 A 28.916 28.916 0 0 1 104.395 30.781 A 13.867 13.867 0 0 1 101.492 30.49 A 10.794 10.794 0 0 1 98.799 29.541 Q 96.387 28.301 95.029 25.84 Q 93.799 23.61 93.684 20.401 A 18.776 18.776 0 0 1 93.672 19.727 Q 93.672 16.507 94.639 14.146 A 10.342 10.342 0 0 1 94.903 13.555 Q 96.133 11.016 98.36 9.688 A 9.368 9.368 0 0 1 101.944 8.464 A 12.008 12.008 0 0 1 103.555 8.359 A 11.755 11.755 0 0 1 105.927 8.587 A 8.647 8.647 0 0 1 108.516 9.551 Q 110.586 10.742 111.699 12.93 A 9.888 9.888 0 0 1 112.592 15.621 Q 112.806 16.77 112.812 18.079 A 16.579 16.579 0 0 1 112.813 18.164 Z M 122.598 0 L 122.598 30.391 L 117.988 30.391 L 117.988 0 L 122.598 0 Z M 81.758 30.293 L 78.36 30.293 A 6.849 6.849 0 0 0 77.198 30.387 A 5.155 5.155 0 0 0 76.113 30.693 A 3.751 3.751 0 0 0 75.247 31.184 A 3.083 3.083 0 0 0 74.6 31.855 A 2.918 2.918 0 0 0 74.102 33.135 A 3.843 3.843 0 0 0 74.063 33.691 Q 74.063 35.176 75.381 35.977 A 4.739 4.739 0 0 0 76.552 36.472 Q 77.123 36.634 77.812 36.71 A 11.982 11.982 0 0 0 79.121 36.777 A 20.524 20.524 0 0 0 80.816 36.711 Q 82.139 36.602 83.164 36.309 A 7.881 7.881 0 0 0 84.198 35.939 Q 85.094 35.541 85.645 34.971 A 2.865 2.865 0 0 0 86.485 32.91 A 3.38 3.38 0 0 0 86.429 32.278 Q 86.361 31.918 86.207 31.637 A 1.712 1.712 0 0 0 85.957 31.289 Q 85.52 30.819 84.719 30.591 A 4.635 4.635 0 0 0 84.375 30.508 A 9.365 9.365 0 0 0 83.513 30.377 Q 82.735 30.293 81.758 30.293 Z M 80.02 19.941 A 5.818 5.818 0 0 0 81.15 19.839 Q 81.795 19.711 82.29 19.424 A 2.885 2.885 0 0 0 83.008 18.838 Q 83.985 17.734 83.985 15.723 Q 83.985 14.316 83.569 13.362 A 3.284 3.284 0 0 0 82.979 12.441 Q 81.973 11.348 80 11.348 A 5.351 5.351 0 0 0 78.838 11.466 Q 78.082 11.634 77.525 12.041 A 3.06 3.06 0 0 0 77.061 12.461 A 3.522 3.522 0 0 0 76.347 13.715 Q 76.167 14.251 76.098 14.905 A 8.171 8.171 0 0 0 76.055 15.762 Q 76.055 17.284 76.654 18.289 A 3.389 3.389 0 0 0 77.061 18.838 A 3.211 3.211 0 0 0 78.589 19.759 Q 79.107 19.907 79.728 19.935 A 6.543 6.543 0 0 0 80.02 19.941 Z M 4.805 17.344 L 14.649 17.344 A 8.913 8.913 0 0 0 14.509 15.838 A 6.753 6.753 0 0 0 14.112 14.473 A 4.468 4.468 0 0 0 13.291 13.159 A 4.083 4.083 0 0 0 12.549 12.52 A 3.933 3.933 0 0 0 11.255 11.966 Q 10.728 11.84 10.114 11.82 A 7.194 7.194 0 0 0 9.883 11.816 A 5.43 5.43 0 0 0 8.259 12.048 A 4.345 4.345 0 0 0 6.377 13.223 A 5.082 5.082 0 0 0 5.353 14.86 Q 4.928 15.923 4.805 17.344 Z M 53.262 17.344 L 63.106 17.344 A 8.913 8.913 0 0 0 62.966 15.838 A 6.753 6.753 0 0 0 62.569 14.473 A 4.468 4.468 0 0 0 61.748 13.159 A 4.083 4.083 0 0 0 61.006 12.52 A 3.933 3.933 0 0 0 59.712 11.966 Q 59.185 11.84 58.571 11.82 A 7.194 7.194 0 0 0 58.34 11.816 A 5.43 5.43 0 0 0 56.716 12.048 A 4.345 4.345 0 0 0 54.834 13.223 A 5.082 5.082 0 0 0 53.81 14.86 Q 53.385 15.923 53.262 17.344 Z M 98.477 17.344 L 108.32 17.344 A 8.913 8.913 0 0 0 108.181 15.838 A 6.753 6.753 0 0 0 107.783 14.473 A 4.468 4.468 0 0 0 106.963 13.159 A 4.083 4.083 0 0 0 106.221 12.52 A 3.933 3.933 0 0 0 104.927 11.966 Q 104.4 11.84 103.785 11.82 A 7.194 7.194 0 0 0 103.555 11.816 A 5.43 5.43 0 0 0 101.93 12.048 A 4.345 4.345 0 0 0 100.049 13.223 A 5.082 5.082 0 0 0 99.024 14.86 Q 98.599 15.923 98.477 17.344 Z"\n' +
      '    fill="currentColor"\n' +
      '    transform="translate(55,0)"\n' +
      '  ></path>\n' +
      '</svg>\n'
    );
  }

  static getCSS() {
    return (
      'body {\n' +
      '    counter-reset: a b\n' +
      '}\n' +
      '\n' +
      '.prism *,.token.pre.gfm * {\n' +
      '    font-weight: inherit!important\n' +
      '}\n' +
      '\n' +
      '.prism .token.cdata,.prism .token.comment,.prism .token.doctype,.prism .token.prolog,.token.pre.gfm .token.cdata,.token.pre.gfm .token.comment,.token.pre.gfm .token.doctype,.token.pre.gfm .token.prolog {\n' +
      '    color: #708090\n' +
      '}\n' +
      '\n' +
      '.prism .token.punctuation,.token.pre.gfm .token.punctuation {\n' +
      '    color: #999\n' +
      '}\n' +
      '\n' +
      '.prism .namespace,.token.pre.gfm .namespace {\n' +
      '    opacity: .7\n' +
      '}\n' +
      '\n' +
      '.prism .token.boolean,.prism .token.constant,.prism .token.deleted,.prism .token.number,.prism .token.property,.prism .token.symbol,.prism .token.tag,.token.pre.gfm .token.boolean,.token.pre.gfm .token.constant,.token.pre.gfm .token.deleted,.token.pre.gfm .token.number,.token.pre.gfm .token.property,.token.pre.gfm .token.symbol,.token.pre.gfm .token.tag {\n' +
      '    color: #905\n' +
      '}\n' +
      '\n' +
      '.prism .token.attr-name,.prism .token.builtin,.prism .token.char,.prism .token.inserted,.prism .token.selector,.prism .token.string,.token.pre.gfm .token.attr-name,.token.pre.gfm .token.builtin,.token.pre.gfm .token.char,.token.pre.gfm .token.inserted,.token.pre.gfm .token.selector,.token.pre.gfm .token.string {\n' +
      '    color: #690\n' +
      '}\n' +
      '\n' +
      '.prism .language-css .token.string,.prism .style .token.string,.prism .token.entity,.prism .token.operator,.prism .token.url,.token.pre.gfm .language-css .token.string,.token.pre.gfm .style .token.string,.token.pre.gfm .token.entity,.token.pre.gfm .token.operator,.token.pre.gfm .token.url {\n' +
      '    color: #a67f59\n' +
      '}\n' +
      '\n' +
      '.prism .token.atrule,.prism .token.attr-value,.prism .token.keyword,.token.pre.gfm .token.atrule,.token.pre.gfm .token.attr-value,.token.pre.gfm .token.keyword {\n' +
      '    color: #07a\n' +
      '}\n' +
      '\n' +
      '.prism .token.function,.token.pre.gfm .token.function {\n' +
      '    color: #dd4a68\n' +
      '}\n' +
      '\n' +
      '.prism .token.important,.prism .token.regex,.prism .token.variable,.token.pre.gfm .token.important,.token.pre.gfm .token.regex,.token.pre.gfm .token.variable {\n' +
      '    color: #e90\n' +
      '}\n' +
      '\n' +
      '.prism .token.bold,.prism .token.important,.token.pre.gfm .token.bold,.token.pre.gfm .token.important {\n' +
      '    font-weight: 500\n' +
      '}\n' +
      '\n' +
      '.prism .token.italic,.token.pre.gfm .token.italic {\n' +
      '    font-style: italic\n' +
      '}\n' +
      '\n' +
      '/*! normalize-scss | MIT/GPLv2 License | bit.ly/normalize-scss */\n' +
      'html {\n' +
      '    line-height: 1.15;\n' +
      '    -ms-text-size-adjust: 100%;\n' +
      '    -webkit-text-size-adjust: 100%\n' +
      '}\n' +
      '\n' +
      'body {\n' +
      '    margin: 0\n' +
      '}\n' +
      '\n' +
      'article,aside,footer,header,nav,section {\n' +
      '    display: block\n' +
      '}\n' +
      '\n' +
      'h1 {\n' +
      '    font-size: 2em;\n' +
      '    margin: .67em 0\n' +
      '}\n' +
      '\n' +
      'figcaption,figure {\n' +
      '    display: block\n' +
      '}\n' +
      '\n' +
      'figure {\n' +
      '    margin: 1em 40px\n' +
      '}\n' +
      '\n' +
      'hr {\n' +
      '    box-sizing: content-box;\n' +
      '    height: 0;\n' +
      '    overflow: visible\n' +
      '}\n' +
      '\n' +
      'main {\n' +
      '    display: block\n' +
      '}\n' +
      '\n' +
      'pre {\n' +
      '    font-family: monospace,monospace;\n' +
      '    font-size: 1em\n' +
      '}\n' +
      '\n' +
      'a {\n' +
      '    background-color: transparent;\n' +
      '    -webkit-text-decoration-skip: objects\n' +
      '}\n' +
      '\n' +
      'abbr[title] {\n' +
      '    border-bottom: none;\n' +
      '    text-decoration: underline;\n' +
      '    text-decoration: underline dotted\n' +
      '}\n' +
      '\n' +
      'b,strong {\n' +
      '    font-weight: inherit;\n' +
      '    font-weight: bolder\n' +
      '}\n' +
      '\n' +
      'code,kbd,samp {\n' +
      '    font-family: monospace,monospace;\n' +
      '    font-size: 1em\n' +
      '}\n' +
      '\n' +
      'dfn {\n' +
      '    font-style: italic\n' +
      '}\n' +
      '\n' +
      'mark {\n' +
      '    background-color: #ff0;\n' +
      '    color: #000\n' +
      '}\n' +
      '\n' +
      'small {\n' +
      '    font-size: 80%\n' +
      '}\n' +
      '\n' +
      'sub,sup {\n' +
      '    font-size: 75%;\n' +
      '    line-height: 0;\n' +
      '    position: relative;\n' +
      '    vertical-align: baseline\n' +
      '}\n' +
      '\n' +
      'sub {\n' +
      '    bottom: -.25em\n' +
      '}\n' +
      '\n' +
      'sup {\n' +
      '    top: -.5em\n' +
      '}\n' +
      '\n' +
      'audio,video {\n' +
      '    display: inline-block\n' +
      '}\n' +
      '\n' +
      'audio:not([controls]) {\n' +
      '    display: none;\n' +
      '    height: 0\n' +
      '}\n' +
      '\n' +
      'img {\n' +
      '    border-style: none\n' +
      '}\n' +
      '\n' +
      'svg:not(:root) {\n' +
      '    overflow: hidden\n' +
      '}\n' +
      '\n' +
      'button,input,optgroup,select,textarea {\n' +
      '    font-family: sans-serif;\n' +
      '    font-size: 100%;\n' +
      '    line-height: 1.15;\n' +
      '    margin: 0\n' +
      '}\n' +
      '\n' +
      'button {\n' +
      '    overflow: visible\n' +
      '}\n' +
      '\n' +
      'button,select {\n' +
      '    text-transform: none\n' +
      '}\n' +
      '\n' +
      '[type=reset],[type=submit],button,html [type=button] {\n' +
      '    -webkit-appearance: button\n' +
      '}\n' +
      '\n' +
      '[type=button]::-moz-focus-inner,[type=reset]::-moz-focus-inner,[type=submit]::-moz-focus-inner,button::-moz-focus-inner {\n' +
      '    border-style: none;\n' +
      '    padding: 0\n' +
      '}\n' +
      '\n' +
      '[type=button]:-moz-focusring,[type=reset]:-moz-focusring,[type=submit]:-moz-focusring,button:-moz-focusring {\n' +
      '    outline: 1px dotted ButtonText\n' +
      '}\n' +
      '\n' +
      'input {\n' +
      '    overflow: visible\n' +
      '}\n' +
      '\n' +
      '[type=checkbox],[type=radio] {\n' +
      '    box-sizing: border-box;\n' +
      '    padding: 0\n' +
      '}\n' +
      '\n' +
      '[type=number]::-webkit-inner-spin-button,[type=number]::-webkit-outer-spin-button {\n' +
      '    height: auto\n' +
      '}\n' +
      '\n' +
      '[type=search] {\n' +
      '    -webkit-appearance: textfield;\n' +
      '    outline-offset: -2px\n' +
      '}\n' +
      '\n' +
      '[type=search]::-webkit-search-cancel-button,[type=search]::-webkit-search-decoration {\n' +
      '    -webkit-appearance: none\n' +
      '}\n' +
      '\n' +
      '::-webkit-file-upload-button {\n' +
      '    -webkit-appearance: button;\n' +
      '    font: inherit\n' +
      '}\n' +
      '\n' +
      'fieldset {\n' +
      '    padding: .35em .75em .625em\n' +
      '}\n' +
      '\n' +
      'legend {\n' +
      '    box-sizing: border-box;\n' +
      '    display: table;\n' +
      '    max-width: 100%;\n' +
      '    padding: 0;\n' +
      '    color: inherit;\n' +
      '    white-space: normal\n' +
      '}\n' +
      '\n' +
      'progress {\n' +
      '    display: inline-block;\n' +
      '    vertical-align: baseline\n' +
      '}\n' +
      '\n' +
      'textarea {\n' +
      '    overflow: auto\n' +
      '}\n' +
      '\n' +
      'details {\n' +
      '    display: block\n' +
      '}\n' +
      '\n' +
      'summary {\n' +
      '    display: list-item\n' +
      '}\n' +
      '\n' +
      'menu {\n' +
      '    display: block\n' +
      '}\n' +
      '\n' +
      'canvas {\n' +
      '    display: inline-block\n' +
      '}\n' +
      '\n' +
      '[hidden],template {\n' +
      '    display: none\n' +
      '}\n' +
      '\n' +
      'body,html {\n' +
      '    color: rgba(0,0,0,.75);\n' +
      '    font-size: 16px;\n' +
      '    font-family: Lato,Helvetica Neue,Helvetica,sans-serif;\n' +
      '    font-variant-ligatures: common-ligatures;\n' +
      '    line-height: 1.67;\n' +
      '    -webkit-font-smoothing: antialiased;\n' +
      '    -moz-osx-font-smoothing: grayscale\n' +
      '}\n' +
      '\n' +
      '.app--dark .layout__panel--editor,.app--dark .layout__panel--preview {\n' +
      '    color: hsla(0,0%,100%,.75)\n' +
      '}\n' +
      '\n' +
      'blockquote,dl,ol,p,pre,ul {\n' +
      '    margin: 1.2em 0\n' +
      '}\n' +
      '\n' +
      'h1,h2,h3,h4,h5,h6 {\n' +
      '    margin: 1.8em 0;\n' +
      '    line-height: 1.33\n' +
      '}\n' +
      '\n' +
      'h1:after,h2:after {\n' +
      '    content: "";\n' +
      '    display: block;\n' +
      '    position: relative;\n' +
      '    top: .33em;\n' +
      '    border-bottom: 1px solid hsla(0,0%,50%,.33)\n' +
      '}\n' +
      '\n' +
      'ol ol,ol ul,ul ol,ul ul {\n' +
      '    margin: 0\n' +
      '}\n' +
      '\n' +
      'dt {\n' +
      '    font-weight: 700\n' +
      '}\n' +
      '\n' +
      'a {\n' +
      '    color: #0c93e4;\n' +
      '    text-decoration: underline;\n' +
      '    text-decoration-skip: ink\n' +
      '}\n' +
      '\n' +
      'a:focus,a:hover {\n' +
      '    text-decoration: none\n' +
      '}\n' +
      '\n' +
      'code,pre,samp {\n' +
      '    font-family: Roboto Mono,Lucida Sans Typewriter,Lucida Console,monaco,Courrier,monospace;\n' +
      '    font-size: .85em\n' +
      '}\n' +
      '\n' +
      'code *,pre *,samp * {\n' +
      '    font-size: inherit\n' +
      '}\n' +
      '\n' +
      'blockquote {\n' +
      '    color: rgba(0,0,0,.5);\n' +
      '    padding-left: 1.5em;\n' +
      '    border-left: 5px solid rgba(0,0,0,.1)\n' +
      '}\n' +
      '\n' +
      '.app--dark .layout__panel--editor blockquote,.app--dark .layout__panel--preview blockquote {\n' +
      '    color: hsla(0,0%,100%,.4);\n' +
      '    border-left-color: hsla(0,0%,100%,.1)\n' +
      '}\n' +
      '\n' +
      'code {\n' +
      '    background-color: rgba(0,0,0,.05);\n' +
      '    border-radius: 3px;\n' +
      '    padding: 2px 4px\n' +
      '}\n' +
      '\n' +
      'hr {\n' +
      '    border: 0;\n' +
      '    border-top: 1px solid hsla(0,0%,50%,.33);\n' +
      '    margin: 2em 0\n' +
      '}\n' +
      '\n' +
      'pre>code {\n' +
      '    background-color: rgba(0,0,0,.05);\n' +
      '    display: block;\n' +
      '    padding: .5em;\n' +
      '    -webkit-text-size-adjust: none;\n' +
      '    overflow-x: auto;\n' +
      '    white-space: pre\n' +
      '}\n' +
      '\n' +
      '.toc ul {\n' +
      '    list-style-type: none;\n' +
      '    padding-left: 20px\n' +
      '}\n' +
      '\n' +
      'table {\n' +
      '    background-color: transparent;\n' +
      '    border-collapse: collapse;\n' +
      '    border-spacing: 0\n' +
      '}\n' +
      '\n' +
      'td,th {\n' +
      '    border-right: 1px solid #dcdcdc;\n' +
      '    padding: 8px 12px\n' +
      '}\n' +
      '\n' +
      'td:last-child,th:last-child {\n' +
      '    border-right: 0\n' +
      '}\n' +
      '\n' +
      'td {\n' +
      '    border-top: 1px solid #dcdcdc\n' +
      '}\n' +
      '\n' +
      'mark {\n' +
      '    background-color: #f8f840\n' +
      '}\n' +
      '\n' +
      'kbd {\n' +
      '    font-family: Lato,Helvetica Neue,Helvetica,sans-serif;\n' +
      '    background-color: #fff;\n' +
      '    border: 1px solid rgba(63,63,63,.25);\n' +
      '    border-radius: 3px;\n' +
      '    box-shadow: 0 1px 0 rgba(63,63,63,.25);\n' +
      '    color: #333;\n' +
      '    display: inline-block;\n' +
      '    font-size: .8em;\n' +
      '    margin: 0 .1em;\n' +
      '    padding: .1em .6em;\n' +
      '    white-space: nowrap\n' +
      '}\n' +
      '\n' +
      'abbr[title] {\n' +
      '    border-bottom: 1px dotted #777;\n' +
      '    cursor: help\n' +
      '}\n' +
      '\n' +
      'img {\n' +
      '    max-width: 100%\n' +
      '}\n' +
      '\n' +
      '.task-list-item {\n' +
      '    list-style-type: none\n' +
      '}\n' +
      '\n' +
      '.task-list-item-checkbox {\n' +
      '    margin: 0 .2em 0 -1.3em\n' +
      '}\n' +
      '\n' +
      '.footnote {\n' +
      '    font-size: .8em;\n' +
      '    position: relative;\n' +
      '    top: -.25em;\n' +
      '    vertical-align: top\n' +
      '}\n' +
      '\n' +
      '.page-break-after {\n' +
      '    page-break-after: always\n' +
      '}\n' +
      '\n' +
      '.abc-notation-block {\n' +
      '    overflow-x: auto!important\n' +
      '}\n' +
      '\n' +
      '.stackedit__html {\n' +
      '    margin-bottom: 180px;\n' +
      '    margin-left: auto;\n' +
      '    margin-right: auto;\n' +
      '    padding-left: 30px;\n' +
      '    padding-right: 30px;\n' +
      '    max-width: 750px\n' +
      '}\n' +
      '\n' +
      '.stackedit__toc ul {\n' +
      '    padding: 0\n' +
      '}\n' +
      '\n' +
      '.stackedit__toc ul a {\n' +
      '    margin: .5rem 0;\n' +
      '    padding: .5rem 1rem\n' +
      '}\n' +
      '\n' +
      '.stackedit__toc ul ul {\n' +
      '    color: #888;\n' +
      '    font-size: .9em\n' +
      '}\n' +
      '\n' +
      '.stackedit__toc ul ul a {\n' +
      '    margin: 0;\n' +
      '    padding: .1rem 1rem\n' +
      '}\n' +
      '\n' +
      '.stackedit__toc li {\n' +
      '    display: block\n' +
      '}\n' +
      '\n' +
      '.stackedit__toc a {\n' +
      '    display: block;\n' +
      '    color: inherit;\n' +
      '    text-decoration: none\n' +
      '}\n' +
      '\n' +
      '.stackedit__toc a:active,.stackedit__toc a:focus,.stackedit__toc a:hover {\n' +
      '    background-color: rgba(0,0,0,.075);\n' +
      '    border-radius: 3px\n' +
      '}\n' +
      '\n' +
      '.stackedit__left {\n' +
      '    position: fixed;\n' +
      '    display: none;\n' +
      '    width: 250px;\n' +
      '    height: 100%;\n' +
      '    top: 0;\n' +
      '    left: 0;\n' +
      '    overflow-x: hidden;\n' +
      '    overflow-y: auto;\n' +
      '    -webkit-overflow-scrolling: touch;\n' +
      '    -ms-overflow-style: none\n' +
      '}\n' +
      '\n' +
      '@media (min-width: 1060px) {\n' +
      '    .stackedit__left {\n' +
      '        display:block\n' +
      '    }\n' +
      '}\n' +
      '\n' +
      '.stackedit__right {\n' +
      '    position: absolute;\n' +
      '    right: 0;\n' +
      '    top: 0;\n' +
      '    left: 0\n' +
      '}\n' +
      '\n' +
      '@media (min-width: 1060px) {\n' +
      '    .stackedit__right {\n' +
      '        left:250px\n' +
      '    }\n' +
      '}\n' +
      '\n' +
      '.stackedit--pdf blockquote {\n' +
      '    border-left-color: #ececec\n' +
      '}\n' +
      '\n' +
      '.stackedit--pdf .stackedit__html {\n' +
      '    padding-left: 0;\n' +
      '    padding-right: 0;\n' +
      '    max-width: none\n' +
      '}\n'
    );
  }
}
